/**
 * A trial has ended when the backend flags it expired OR its day count has reached
 * zero/negative. The backend can return `isExpired: false` alongside a negative
 * `remainingDays`, so both conditions must be checked — otherwise the UI renders a
 * negative "days left". Absent/undefined days are NOT treated as ended (avoids a
 * false-expired flash while the subscription is still loading).
 */
export const trialHasEnded = (
  isExpired: boolean | undefined,
  remainingDays: number | null | undefined,
): boolean => !!isExpired || (remainingDays != null && remainingDays <= 0);

/** Trial days remaining, clamped so a count is never negative (0 when ended/absent). */
export const clampTrialDays = (remainingDays: number | null | undefined): number =>
  Math.max(0, remainingDays ?? 0);

/** Day threshold at/under which a trial is "expiring" (urgent). Shared by the
 *  powerspot resolver and trialUrgencyTier so the boundary never drifts. */
export const TRIAL_EXPIRING_DAYS = 3;

export type TrialUrgencyTier = "calm" | "amber" | "red";

/**
 * Classifies an ACTIVE trial's urgency for UI signaling (color/copy tone).
 * Returns null when the trial has ended (trial_expired owns that state).
 * Callers must guard on a loaded subscription — during load this can read 0 days.
 *   calm  — >3 days left   amber — 1–3 days left   red — <1 day left
 */
export const trialUrgencyTier = (
  isExpired: boolean | undefined,
  remainingDays: number | null | undefined,
): TrialUrgencyTier | null => {
  if (trialHasEnded(isExpired, remainingDays)) return null;
  const days = clampTrialDays(remainingDays);
  if (days > TRIAL_EXPIRING_DAYS) return "calm";
  if (days >= 1) return "amber";
  return "red";
};
